import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart3, Users, Eye, Clock, Smartphone, Monitor, Globe, TrendingUp, Zap, Download } from "lucide-react";

const RANGES = [
  { k: "today", label: "Hoy" },
  { k: "7d", label: "7 días" },
  { k: "30d", label: "30 días" },
  { k: "month", label: "Este mes" },
  { k: "6m", label: "6 meses" },
  { k: "custom", label: "Personalizado" },
];

function Stat({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="border rounded-lg p-4" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70">
        <Icon size={13} /> {label}
      </div>
      <div className="font-headline text-3xl md:text-4xl mt-1">{value}</div>
      {hint && <div className="text-[10px] opacity-60 mt-1">{hint}</div>}
    </div>
  );
}

function fmtDur(ms) {
  if (!ms) return "0s";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function Spark({ data }) {
  // Simple inline SVG sparkline from daily series
  if (!data?.length) return null;
  const W = 600, H = 100, P = 4;
  const vals = data.map((d) => d.views);
  const max = Math.max(1, ...vals);
  const step = data.length > 1 ? (W - P * 2) / (data.length - 1) : 0;
  const pts = vals.map((v, i) => `${P + i * step},${H - P - (v / max) * (H - P * 2)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none" data-testid="analytics-spark">
      <polyline points={pts} fill="none" stroke="#ec4899" strokeWidth="2" />
      {vals.map((v, i) => (
        <circle key={i} cx={P + i * step} cy={H - P - (v / max) * (H - P * 2)} r="2.5" fill="#ec4899" />
      ))}
    </svg>
  );
}

export default function AdminAnalyticsTab() {
  const [range, setRange] = useState("7d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [overview, setOverview] = useState(null);
  const [topArts, setTopArts] = useState([]);
  const [sources, setSources] = useState([]);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = range === "custom" && customFrom && customTo
      ? { range: "custom", from_: customFrom, to: customTo }
      : { range };
    try {
      const [ov, ta, src] = await Promise.all([
        api.get("/admin/analytics/overview", { params }).then((r) => r.data),
        api.get("/admin/analytics/top-articles", { params: { ...params, limit: 10 } }).then((r) => r.data),
        api.get("/admin/analytics/sources", { params }).then((r) => r.data),
      ]);
      setOverview(ov);
      setTopArts(ta || []);
      setSources(src || []);
    } finally {
      setLoading(false);
    }
  };

  const loadRealtime = async () => {
    try {
      const r = await api.get("/admin/analytics/realtime");
      setRealtime(r.data);
    } catch (_) { /* noop */ }
  };

  useEffect(() => { load(); loadRealtime(); }, []); // eslint-disable-line
  useEffect(() => { load(); }, [range, customFrom, customTo]); // eslint-disable-line
  useEffect(() => {
    const t = setInterval(loadRealtime, 15000);
    return () => clearInterval(t);
  }, []);

  const downloadCSV = async (kind) => {
    const params = new URLSearchParams();
    if (range === "custom" && customFrom && customTo) {
      params.set("range", "custom");
      params.set("from_", customFrom);
      params.set("to", customTo);
    } else {
      params.set("range", range);
    }
    params.set("kind", kind);
    const token = localStorage.getItem("admin_token");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    try {
      const r = await fetch(`${base}/api/admin/analytics/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = r.headers.get("Content-Disposition") || "";
      const nameMatch = /filename="([^"]+)"/.exec(cd);
      a.href = url;
      a.download = nameMatch ? nameMatch[1] : `popu-analytics-${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("No se pudo descargar: " + e.message);
    }
  };

  const totalDevices = overview ? Object.values(overview.devices || {}).reduce((a, b) => a + b, 0) || 1 : 1;
  const mobilePct = overview ? Math.round(((overview.devices?.mobile || 0) / totalDevices) * 100) : 0;
  const desktopPct = overview ? Math.round(((overview.devices?.desktop || 0) / totalDevices) * 100) : 0;

  return (
    <div data-testid="admin-analytics-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-headline uppercase text-3xl md:text-4xl flex items-center gap-2"><BarChart3 size={26} /> Analytics</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Sistema propio de estadísticas (sin terceros). GTM-5VSK96XP cargado para Google Analytics.</p>
        </div>
        {realtime && (
          <div className="border rounded-lg px-4 py-2 flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid="analytics-realtime">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Ahora mismo</div>
              <div className="font-headline text-2xl leading-none">{realtime.active_now}</div>
            </div>
            <span className="text-[10px] opacity-60 ml-2">activos (últ. 5 min)</span>
          </div>
        )}
      </div>

      {/* Range picker */}
      <div className="flex flex-wrap items-center gap-1 mb-4">
        {RANGES.map((r) => (
          <button
            key={r.k}
            onClick={() => setRange(r.k)}
            className="text-xs px-3 py-1.5 rounded border font-bold uppercase tracking-widest"
            style={{
              borderColor: range === r.k ? "var(--accent)" : "var(--border)",
              background: range === r.k ? "var(--accent)" : "transparent",
              color: range === r.k ? "#fff" : "var(--text)",
            }}
            data-testid={`analytics-range-${r.k}`}
          >
            {r.label}
          </button>
        ))}
        {range === "custom" && (
          <div className="flex gap-2 ml-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-xs px-2 py-1.5 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="analytics-custom-from" />
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-xs px-2 py-1.5 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="analytics-custom-to" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-1">
          <span className="text-[10px] uppercase opacity-60 tracking-widest mr-1">Descargar</span>
          <button onClick={() => downloadCSV("summary")} className="text-xs px-2 py-1.5 rounded border font-bold flex items-center gap-1" style={{ borderColor: "var(--border)" }} data-testid="analytics-download-summary">
            <Download size={11} /> Resumen diario
          </button>
          <button onClick={() => downloadCSV("articles")} className="text-xs px-2 py-1.5 rounded border font-bold flex items-center gap-1" style={{ borderColor: "var(--border)" }} data-testid="analytics-download-articles">
            <Download size={11} /> Artículos
          </button>
          <button onClick={() => downloadCSV("events")} className="text-xs px-2 py-1.5 rounded border font-bold flex items-center gap-1" style={{ borderColor: "var(--border)" }} data-testid="analytics-download-events">
            <Download size={11} /> Eventos (crudo)
          </button>
        </div>
      </div>

      {loading && <p className="text-xs opacity-70 mb-3">Cargando…</p>}

      {/* Top stats */}
      {overview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Stat icon={Eye} label="Vistas totales" value={overview.total_views.toLocaleString("es-BO")} testid="analytics-total-views" />
            <Stat icon={Users} label="Visitantes únicos" value={overview.unique_visitors.toLocaleString("es-BO")} testid="analytics-unique-visitors" />
            <Stat icon={TrendingUp} label="Vistas de artículos" value={overview.article_views.toLocaleString("es-BO")} testid="analytics-article-views" />
            <Stat icon={Clock} label="Tiempo medio" value={fmtDur(overview.avg_duration_ms)} hint="por artículo" testid="analytics-avg-duration" />
          </div>

          {/* Chart */}
          <div className="border rounded-lg p-4 mb-5" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs uppercase tracking-widest font-bold">Vistas diarias</h3>
              <span className="text-[10px] opacity-60">{overview.daily.length} días</span>
            </div>
            <Spark data={overview.daily} />
            <div className="flex gap-4 mt-2 text-[10px] opacity-70">
              {overview.daily.slice(-7).map((d) => (
                <div key={d.date} className="flex-1 text-center">
                  <div className="font-bold text-sm" style={{ color: "var(--text)" }}>{d.views}</div>
                  <div>{d.date.slice(5)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Devices */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <div className="border rounded-lg p-4" style={{ borderColor: "var(--border)" }}>
              <h3 className="text-xs uppercase tracking-widest font-bold mb-3">Dispositivos</h3>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm"><Smartphone size={14} /> Mobile <strong className="ml-auto">{mobilePct}%</strong></div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden mt-1"><div className="h-full bg-pink-500" style={{ width: `${mobilePct}%` }} /></div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm"><Monitor size={14} /> Desktop <strong className="ml-auto">{desktopPct}%</strong></div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden mt-1"><div className="h-full bg-sky-500" style={{ width: `${desktopPct}%` }} /></div>
                </div>
              </div>
            </div>

            {/* Sources */}
            <div className="border rounded-lg p-4" style={{ borderColor: "var(--border)" }} data-testid="analytics-sources">
              <h3 className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-2"><Globe size={14} /> Fuentes de tráfico</h3>
              {sources.length === 0 ? (
                <p className="text-xs opacity-70">Sin datos aún</p>
              ) : (
                <div className="space-y-1">
                  {sources.slice(0, 8).map((s) => (
                    <div key={s.source} className="flex items-center text-sm">
                      <span className="flex-1 truncate">{s.source}</span>
                      <strong>{s.visits}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Top articles */}
          <div className="border rounded-lg p-4 mb-5" style={{ borderColor: "var(--border)" }}>
            <h3 className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-2"><TrendingUp size={14} /> Artículos más leídos</h3>
            {topArts.length === 0 ? (
              <p className="text-xs opacity-70">Sin datos aún — las vistas aparecerán cuando los usuarios lean artículos.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest opacity-70 text-left">
                    <th className="py-1 w-10">#</th>
                    <th className="py-1">Título</th>
                    <th className="py-1 text-right">Vistas</th>
                    <th className="py-1 text-right hidden md:table-cell">Únicos</th>
                    <th className="py-1 text-right hidden md:table-cell">T. medio</th>
                  </tr>
                </thead>
                <tbody>
                  {topArts.map((a, i) => (
                    <tr key={a.slug} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`analytics-top-${a.slug}`}>
                      <td className="py-2 opacity-60">{i + 1}</td>
                      <td className="py-2 truncate max-w-xs">
                        <a href={`/nota/${a.slug}`} target="_blank" rel="noreferrer" className="hover:text-pink-500">
                          {a.title || a.slug}
                        </a>
                        {a.category_slug && <span className="ml-2 text-[10px] opacity-60 uppercase">{a.category_slug}</span>}
                      </td>
                      <td className="py-2 text-right font-bold">{a.views}</td>
                      <td className="py-2 text-right opacity-70 hidden md:table-cell">{a.unique_visitors}</td>
                      <td className="py-2 text-right opacity-70 hidden md:table-cell">{fmtDur(a.avg_duration_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Realtime paths */}
          {realtime?.paths?.length > 0 && (
            <div className="border rounded-lg p-4" style={{ borderColor: "var(--border)" }}>
              <h3 className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-2"><Zap size={14} /> Páginas vistas ahora mismo</h3>
              <div className="space-y-1">
                {realtime.paths.map((p) => (
                  <div key={p.path} className="flex text-sm">
                    <span className="flex-1 truncate">{p.path}</span>
                    <strong>{p.views}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
